"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Instagram, Linkedin, Facebook, Youtube, Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, whatsappHref, DEFAULT_WHATSAPP_MESSAGE, GBP_URL, GBP_RATING } from "@/lib/contact";

const socials = [
    { label: "Instagram", href: "https://www.instagram.com/lead_workz?igsh=MWpsanN3OTBjbmgzcA==", icon: Instagram },
    { label: "LinkedIn", href: "https://www.linkedin.com/company/https-www.leadworkz.com-/", icon: Linkedin },
    { label: "Facebook", href: "https://www.facebook.com/share/1CRWRhnrVm/?mibextid=wwXIfr", icon: Facebook },
    { label: "YouTube", href: "https://www.youtube.com/@leadworkz", icon: Youtube },
];

/* ─── Services Data ─────────────────────────────── */
const services = [
    { label: "UI/UX Design", href: "/services/ux-design" },
    { label: "Web Development", href: "/services/web-development" },
    { label: "Search & Visibility", href: "/services/search-visibility" },
    { label: "Performance Marketing", href: "/services/performance-marketing" },
    { label: "Social Media Marketing", href: "/services/social-media" },
    { label: "Branding & Creative Strategy", href: "/services/brand-strategy" },
];

const menuLinks = [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about" },
    { label: "Services", href: "/services" },
    { label: "Contact", href: "/contact" },
];

const importantLinks = [
    { label: "FAQs", href: "/#faq" },
    { label: "Case Studies", href: "/our-work/case-studies" },
    { label: "Our Work", href: "/our-work" },
    { label: "Blogs", href: "/blogs" },
    { label: "Privacy Policy", href: "/privacy-policy" },
];

const locations = [
    { label: "Bangalore", href: "/locations/bangalore" },
    { label: "Mumbai", href: "/locations/mumbai" },
    { label: "Delhi", href: "/locations/delhi" },
    { label: "Hyderabad", href: "/locations/hyderabad" },
    { label: "Chennai", href: "/locations/chennai" },
    { label: "Pune", href: "/locations/pune" },
    { label: "Kolkata", href: "/locations/kolkata" },
    { label: "Ahmedabad", href: "/locations/ahmedabad" },
];

/* ─── Footer ────────────────────────────────────── */
// Drops hover/group-hover classes so static (non-link) items don't look clickable
const withoutHover = (className: string) =>
    className
        .split(" ")
        .filter((c) => !c.startsWith("hover:") && !c.startsWith("group-hover:"))
        .join(" ");

function FooterLink({
    href,
    className,
    isStatic,
    external,
    prefetch,
    ariaLabel,
    children,
}: {
    href: string;
    className: string;
    isStatic?: boolean;
    external?: boolean;
    prefetch?: boolean;
    ariaLabel?: string;
    children: React.ReactNode;
}) {
    if (isStatic) {
        return (
            <span className={withoutHover(className)} role={ariaLabel ? "img" : undefined} aria-label={ariaLabel}>
                {children}
            </span>
        );
    }
    if (external) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" aria-label={ariaLabel} className={className}>
                {children}
            </a>
        );
    }
    return (
        <Link href={href} prefetch={prefetch} aria-label={ariaLabel} className={className}>
            {children}
        </Link>
    );
}

export default function Footer({ staticLinks = false }: { staticLinks?: boolean } = {}) {
    const pathname = usePathname();
    const isAnalyticsPage = pathname === "/services/performance-marketing/marketing-analytics";
    // The PPC landing pages are paid-traffic pages — don't spend bandwidth
    // prefetching the rest of the site behind them.
    const prefetch =
        pathname === "/digital-marketing-agency-bangalore" || pathname === "/web-development-company-in-bangalore" ? false : undefined;

    return (
        <footer className="w-full bg-gradient-to-b from-[#00092E] via-[#002A66] to-[#211D62] text-white" data-theme="dark">

            {/* ── Zone 2: Info columns ── */}
            <div className="px-8 md:px-16 py-12 grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">

                {/* Get In Touch */}
                <div>
                    <p className="text-[10px] font-mono tracking-[0.25em] text-white/50 uppercase mb-5 flex items-center gap-3">
                        Get In Touch
                        <span className="flex-1 h-px bg-white/15" />
                    </p>
                    <address className="not-italic text-white/65 text-sm leading-relaxed">
                        #60 Doddakallasandra Kanakapura Main Road,<br />
                        Near Doddakallasandra Metro Station,<br />
                        Bangalore – 560062
                    </address>
                    <FooterLink
                        href="mailto:info@leadworkz.com"
                        isStatic={staticLinks}
                        className="inline-flex items-center gap-2 text-[#CEFF1A] hover:text-white text-sm font-semibold mt-3 transition-colors duration-200"
                    >
                        <Mail className="w-4 h-4" />
                        info@leadworkz.com
                    </FooterLink>
                    <div className="flex flex-col gap-2 mt-2">
                        <FooterLink
                            href={PHONE_TEL}
                            isStatic={staticLinks}
                            className="inline-flex w-fit items-center gap-2 text-[#CEFF1A] hover:text-white text-sm font-semibold transition-colors duration-200"
                        >
                            <Phone className="w-4 h-4" />
                            {PHONE_DISPLAY}
                        </FooterLink>
                        <FooterLink
                            href={whatsappHref(DEFAULT_WHATSAPP_MESSAGE)}
                            external
                            isStatic={staticLinks}
                            className="inline-flex w-fit items-center gap-2 text-[#CEFF1A] hover:text-white text-sm font-semibold transition-colors duration-200"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Chat on WhatsApp
                        </FooterLink>
                        <FooterLink
                            href={GBP_URL}
                            external
                            isStatic={staticLinks}
                            className="inline-flex w-fit items-center gap-2 text-[#CEFF1A] hover:text-white text-sm font-semibold transition-colors duration-200"
                        >
                            <MapPin className="w-4 h-4" />
                            {GBP_RATING}★ on Google
                        </FooterLink>
                    </div>
                    <div className="flex gap-3 mt-5">
                        {socials.map(({ label, href, icon: Icon }) => (
                            <FooterLink
                                key={label}
                                href={href}
                                external
                                isStatic={staticLinks}
                                ariaLabel={label}
                                className="w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white/65 hover:text-white hover:bg-[#0047AB] hover:border-[#0047AB] transition-all duration-300"
                            >
                                <Icon className="w-4 h-4" />
                            </FooterLink>
                        ))}
                    </div>
                </div>

                {/* Menu */}
                <div>
                    <p className="text-[10px] font-mono tracking-[0.25em] text-white/50 uppercase mb-5 flex items-center gap-3">
                        Menu
                        <span className="flex-1 h-px bg-white/15" />
                    </p>
                    <ul className="flex flex-col gap-2">
                        {menuLinks.map((l) => (
                            <li key={l.label}>
                                <FooterLink
                                    href={l.href}
                                    prefetch={prefetch}
                                    isStatic={staticLinks}
                                    className="text-sm text-white/65 hover:text-white transition-colors duration-200 uppercase tracking-wider font-light"
                                >
                                    {l.label}
                                </FooterLink>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Important Links — Pushed Left slightly via grid adjustment */}
                <div>
                    <p className="text-[10px] font-mono tracking-[0.25em] text-white/50 uppercase mb-5 flex items-center gap-3">
                        Important Links
                        <span className="flex-1 h-px bg-white/15" />
                    </p>
                    <ul className="flex flex-col gap-2">
                        {importantLinks.map((l) => (
                            <li key={l.label}>
                                <FooterLink
                                    href={l.href}
                                    prefetch={prefetch}
                                    isStatic={staticLinks}
                                    className="text-sm text-white/65 hover:text-white transition-colors duration-200 uppercase tracking-wider font-light"
                                >
                                    {l.label}
                                </FooterLink>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Duplicated Services Column */}
                <div>
                    <p className="text-[10px] font-mono tracking-[0.25em] text-white/50 uppercase mb-5 flex items-center gap-3">
                        Services
                        <span className="flex-1 h-px bg-white/15" />
                    </p>
                    <ul className="flex flex-col gap-2">
                        {services.map((s) => (
                            <li key={s.label}>
                                <FooterLink
                                    href={s.href}
                                    prefetch={prefetch}
                                    isStatic={staticLinks}
                                    className="text-sm text-white/65 hover:text-white transition-colors duration-200 uppercase tracking-wider font-light"
                                >
                                    {s.label}
                                </FooterLink>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* ── Locations ── */}
            <div className="px-8 md:px-16 pb-4">
                <p className="text-[10px] font-mono tracking-[0.25em] text-white/50 uppercase mb-5 flex items-center gap-3">
                    Locations We Serve
                    <span className="flex-1 h-px bg-white/15" />
                </p>
                <ul className="flex flex-wrap gap-2.5">
                    {locations.map((l) => (
                        <li key={l.label}>
                            <FooterLink
                                href={l.href}
                                prefetch={prefetch}
                                isStatic={staticLinks}
                                className="group inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs text-white/65 uppercase tracking-wider font-light hover:text-white hover:bg-[#0047AB] hover:border-[#0047AB] transition-all duration-300"
                            >
                                <MapPin className={`w-3.5 h-3.5 text-[#CEFF1A]/70 transition-colors duration-300 ${staticLinks ? "" : "group-hover:text-[#CEFF1A]"}`} />
                                {l.label}
                            </FooterLink>
                        </li>
                    ))}
                </ul>
            </div>

            {/* ── Zone 3: Brand Stamp ── */}
            <div className="relative overflow-hidden px-8 md:px-16 pt-14 pb-0">
                {/* Tagline */}
                <p className="text-white/80 text-sm md:text-base font-light tracking-wide max-w-xs mb-6">
                    Engineering growth systems for brands that mean business.
                </p>

                {/* Giant wordmark — live text */}
                <div className={`w-full ${isAnalyticsPage ? "opacity-100" : "opacity-25"} pointer-events-none select-none flex flex-col items-start`}>
                    <h2
                        className="font-bebas text-white uppercase leading-none"
                        style={{ fontSize: "clamp(4rem, 15vw, 12rem)", letterSpacing: "0.02em" }}
                    >
                        LEAD WORKZ
                    </h2>
                    <p
                        className="font-bebas text-white uppercase tracking-[0.2em] mt-2 text-[clamp(0.5rem, 2vw, 1.5rem)]"
                    >
                        BUILD FOR VISIBILITY. OPTIMIZED FOR REVENUE.
                    </p>
                </div>

                {/* Copyright bar */}
                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-3 border-t border-white/20 py-4 mt-0">
                    <p className="text-white/60 text-xs">© 2026 Lead Workz. All Rights Reserved.</p>
                    <FooterLink
                        href="/privacy-policy"
                        prefetch={prefetch}
                        isStatic={staticLinks}
                        className="text-white/60 hover:text-white text-xs transition-colors duration-200"
                    >
                        Privacy Policy
                    </FooterLink>
                </div>
            </div>
        </footer>
    );
}
